import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { sq, useTitle } from "@/lib/img";
import { MaskedLines, FadeUp, ClipReveal, EASE } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";

function buildRows(images) {
  // Portrait sources (split halves and admin uploads) must never land in a
  // wide full-bleed row where object-cover would slice them; group consecutive
  // portraits into pairs and give landscape images the full-width rows.
  const isPortrait = (u) => u.startsWith("/images/split/") || u.startsWith("/api/files/");
  const rows = [];
  let i = 0;
  let flat = 0;
  while (i < images.length) {
    if (!isPortrait(images[i])) {
      rows.push({ type: "full", items: [images[i]], start: flat });
      i += 1;
      flat += 1;
    } else {
      const items = [images[i]];
      if (i + 1 < images.length && isPortrait(images[i + 1])) items.push(images[i + 1]);
      rows.push({ type: "pair", items, start: flat });
      i += items.length;
      flat += items.length;
    }
  }
  return rows;
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "6%"]);

  useEffect(() => {
    setProject(null);
    setNotFound(false);
    setLightbox(null);
    api
      .get(`/projects/${slug}`)
      .then(({ data }) => setProject(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useTitle(
    project ? `${project.name} | Medium Plenty` : "Medium Plenty",
    project
      ? `${project.name} — a ${project.category.toLowerCase()} project in ${project.location} by Medium Plenty, a California architecture and interior design studio.`
      : undefined,
    project ? sq(project.hero_image, 1200) : undefined
  );

  if (notFound)
    return (
      <div data-testid="project-not-found" className="pt-48 pb-40 px-6 md:px-12 min-h-screen">
        <p className="font-serif font-light text-5xl">Project not found.</p>
        <Link to="/projects" className="editorial-link inline-flex items-center gap-2 mt-8 text-sm">
          <ArrowLeft size={15} /> All Projects
        </Link>
      </div>
    );

  if (!project)
    return <div data-testid="project-loading" className="min-h-screen bg-paper" />;

  const meta = [
    ["Location", project.location],
    ["Typology", project.category],
    ["Featured Medium", project.featured_medium],
    ["Scope", project.scope],
  ].filter(([, v]) => v);

  return (
    <div data-testid="project-detail-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[88svh] min-h-[540px] overflow-hidden bg-ink">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <motion.img
            src={sq(project.hero_image, 2000)}
            alt={project.name}
            className="h-[118%] w-full object-cover"
            initial={{ scale: 1.06, opacity: 0.8 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.4, ease: EASE }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-ink/25" />
        <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 pb-12 md:pb-16 flex items-end justify-between gap-6">
          <h1 className="font-serif font-light text-paper text-[12vw] md:text-[7.5vw] leading-[0.95] tracking-tight">
            <MaskedLines lines={[project.name]} delay={0.3} />
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="hidden md:block text-[11px] uppercase tracking-[0.3em] text-paper/80 whitespace-nowrap"
          >
            {project.category} — {project.location}
          </motion.p>
        </div>
      </section>

      {/* META */}
      <section className="px-6 md:px-12 py-16 md:py-24">
        <FadeUp className="grid grid-cols-2 md:grid-cols-5 gap-y-10 border-y border-line py-10">
          {meta.map(([label, value]) => (
            <div key={label}>
              <p className="kicker mb-3">{label}</p>
              <p data-testid={`project-meta-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-serif text-base md:text-2xl font-light">
                {value}
              </p>
            </div>
          ))}
        </FadeUp>

        <div className="grid md:grid-cols-12 gap-10 mt-16 md:mt-24">
          <FadeUp className="md:col-span-3">
            <p className="kicker">About the Project</p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-8 md:col-start-5">
            <p data-testid="project-description" className="font-sans font-light text-base md:text-xl leading-relaxed text-ink">
              {project.description}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* GALLERY */}
      <section data-testid="project-gallery" className="px-6 md:px-12 pb-24 md:pb-36 space-y-6 md:space-y-10">
        {buildRows(project.images).map((row, ri) =>
          row.type === "full" ? (
            <ClipReveal
              key={ri}
              src={sq(row.items[0], 1800)}
              alt={`${project.name} — view ${row.start + 1}`}
              className="aspect-[4/3] md:aspect-[21/10] bg-sand"
              onClick={() => setLightbox(row.start)}
              testid={`gallery-image-${row.start}`}
            />
          ) : (
            <div key={ri} className="grid grid-cols-2 gap-6 md:gap-10">
              {row.items.map((img, ii) => (
                <ClipReveal
                  key={ii}
                  src={sq(img, 1200)}
                  alt={`${project.name} — detail ${row.start + ii + 1}`}
                  className={`aspect-[3/4] bg-sand ${ii === 1 ? "mt-10 md:mt-28" : ""}`}
                  delay={ii * 0.12}
                  onClick={() => setLightbox(row.start + ii)}
                  testid={`gallery-image-${row.start + ii}`}
                />
              ))}
            </div>
          )
        )}
      </section>

      {/* CREDITS */}
      {project.credits?.length > 0 && (
        <section className="px-6 md:px-12 pb-24 md:pb-36">
          <div className="grid md:grid-cols-12 gap-10">
            <FadeUp className="md:col-span-3">
              <p className="kicker">Collaborators</p>
            </FadeUp>
            <div className="md:col-span-8 md:col-start-5">
              {project.credits.map((c, i) => (
                <FadeUp key={i} delay={i * 0.05}>
                  <div className="flex flex-col md:flex-row md:items-baseline justify-between gap-1 border-t border-line py-5">
                    <span className="text-[11px] uppercase tracking-[0.25em] text-smoke">{c.role}</span>
                    {c.link ? (
                      <a
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`credit-link-${i}`}
                        className="font-serif font-light text-xl md:text-2xl editorial-link"
                      >
                        {c.name}
                      </a>
                    ) : (
                      <span className="font-serif font-light text-xl md:text-2xl">{c.name}</span>
                    )}
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NEXT PROJECT */}
      {project.next_project && (
        <Link
          to={`/projects/${project.next_project.slug}`}
          data-testid="next-project-link"
          className="group relative block h-[60svh] min-h-[420px] overflow-hidden bg-ink"
        >
          <img
            src={sq(project.next_project.hero_image, 1800)}
            alt={project.next_project.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-[1.4s] ease-out group-hover:opacity-90 group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-paper">
            <p className="text-[11px] uppercase tracking-[0.35em] text-paper/70 mb-5">Next Project</p>
            <span className="font-serif font-light text-5xl md:text-8xl tracking-tight inline-flex items-center gap-5">
              {project.next_project.name}
              <ArrowRight size={36} strokeWidth={1} className="transition-transform duration-500 group-hover:translate-x-3" />
            </span>
          </div>
        </Link>
      )}

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox
            images={project.images.map((img) => sq(img, 2400))}
            index={lightbox}
            onClose={() => setLightbox(null)}
            onNavigate={setLightbox}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
